/**
 * Piece-type params (D22): checking a piece's `params` against its type's
 * param specs, and substituting them into the type's timeline templates.
 *
 * **Substitution.** A string that is exactly `{{name}}` becomes the param's
 * typed value (`input: '{{input}}'` with `input: 3` gives `input: 3`, not
 * `'3'` — a vision-mixer input is a number to the driver). A string with
 * `{{name}}` among other text is interpolated. Objects and arrays are walked.
 * An unknown name substitutes as `null` whole, or `''` inside text: the param
 * check has already refused a missing required param, so this is an optional
 * param left out.
 */
import type { JsonValue } from '@norna/domain/primitives.js';
import type { ParamSpec, PieceTypeConfig } from './productionConfig.js';

export type ParamValues = Readonly<Record<string, JsonValue>>;

export type CheckParamsResult = { readonly ok: true; readonly values: ParamValues } | { readonly ok: false; readonly error: string };

type StringSpec = Extract<ParamSpec, { type: 'string' }>;
type NumberSpec = Extract<ParamSpec, { type: 'number' | 'integer' }>;
type ObjectSpec = Extract<ParamSpec, { type: 'object' }>;
type ArraySpec = Extract<ParamSpec, { type: 'array' }>;

/** A checked value, defaults filled in; or what is wrong with it. */
type Checked = { readonly value: JsonValue } | { readonly error: string };

function isRecord(value: JsonValue): value is { [key: string]: JsonValue } {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function checkString(name: string, spec: StringSpec, value: JsonValue): Checked {
  if (typeof value !== 'string') return { error: `param "${name}" must be a string` };
  if (spec.enum !== null && !spec.enum.includes(value)) return { error: `param "${name}" must be one of ${spec.enum.join(', ')}` };
  if (spec.maxLength !== null && value.length > spec.maxLength) return { error: `param "${name}" is longer than ${String(spec.maxLength)}` };
  return { value };
}

function checkNumber(name: string, spec: NumberSpec, value: JsonValue): Checked {
  if (typeof value !== 'number' || !Number.isFinite(value)) return { error: `param "${name}" must be a number` };
  if (spec.type === 'integer' && !Number.isInteger(value)) return { error: `param "${name}" must be an integer` };
  if (spec.enum !== undefined && !spec.enum.includes(value)) return { error: `param "${name}" must be one of ${spec.enum.join(', ')}` };
  if (spec.minimum !== null && value < spec.minimum) return { error: `param "${name}" is below ${String(spec.minimum)}` };
  if (spec.maximum !== null && value > spec.maximum) return { error: `param "${name}" is above ${String(spec.maximum)}` };
  return { value };
}

/** An object's properties, each checked and defaulted as a top-level param is; keys it does not name are kept. */
function checkObject(name: string, spec: ObjectSpec, value: JsonValue): Checked {
  if (!isRecord(value)) return { error: `param "${name}" must be an object` };
  const out: { [key: string]: JsonValue } = { ...value };
  for (const [key, child] of Object.entries(spec.properties)) {
    const result = checkPresent(`${name}.${key}`, child, Object.hasOwn(value, key) ? value[key] : undefined);
    if (result === null) continue;
    if ('error' in result) return result;
    out[key] = result.value;
  }
  return { value: out };
}

function checkArray(name: string, spec: ArraySpec, value: JsonValue): Checked {
  if (!Array.isArray(value)) return { error: `param "${name}" must be a list` };
  if (spec.minItems !== null && value.length < spec.minItems) return { error: `param "${name}" needs at least ${String(spec.minItems)} item(s)` };
  if (spec.maxItems !== null && value.length > spec.maxItems) return { error: `param "${name}" takes at most ${String(spec.maxItems)} item(s)` };
  const out: JsonValue[] = [];
  for (const [index, item] of value.entries()) {
    // An element is always present: a null element is a value that fails its spec, not an absent one.
    const result = item === null ? { error: `param "${name}[${String(index)}]" is empty` } : checkOne(`${name}[${String(index)}]`, spec.items, item);
    if ('error' in result) return result;
    out.push(result.value);
  }
  return { value: out };
}

function checkOne(name: string, spec: ParamSpec, value: JsonValue): Checked {
  switch (spec.type) {
    case 'string':
      return checkString(name, spec, value);
    case 'number':
    case 'integer':
      return checkNumber(name, spec, value);
    case 'boolean':
      return typeof value === 'boolean' ? { value } : { error: `param "${name}" must be a boolean` };
    case 'object':
      return checkObject(name, spec, value);
    case 'array':
      return checkArray(name, spec, value);
  }
}

/** A param as given (undefined: absent), its default filled in; null when it is absent, optional and has no default. */
function checkPresent(name: string, spec: ParamSpec, given: JsonValue | undefined): Checked | null {
  const value = given ?? spec.default;
  if (value === null) return spec.required ? { error: `param "${name}" is required` } : null;
  return checkOne(name, spec, value);
}

/**
 * Check `params` against a piece type, filling defaults (nested objects'
 * too). Unknown params are kept (a newer SPA may write a param an older
 * pinned config does not name, PLAN §6.8) but never checked.
 */
export function checkParams(type: PieceTypeConfig, params: ParamValues): CheckParamsResult {
  const values: Record<string, JsonValue> = { ...params };
  for (const [name, spec] of Object.entries(type.params)) {
    const result = checkPresent(name, spec, Object.hasOwn(params, name) ? params[name] : undefined);
    if (result === null) continue;
    if ('error' in result) return { ok: false, error: result.error };
    values[name] = result.value;
  }
  return { ok: true, values };
}

const WHOLE = /^\{\{\s*([A-Za-z0-9_.:-]+)\s*\}\}$/;
const INLINE = /\{\{\s*([A-Za-z0-9_.:-]+)\s*\}\}/g;

function textOf(value: JsonValue | undefined): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function substituteString(template: string, params: ParamValues): JsonValue {
  const whole = WHOLE.exec(template);
  if (whole !== null) {
    const name = whole[1] ?? '';
    return Object.hasOwn(params, name) ? (params[name] ?? null) : null;
  }
  return template.replace(INLINE, (_match, name: string) => textOf(Object.hasOwn(params, name) ? params[name] : undefined));
}

/** `{{param}}` substitution through a JSON template (module docstring). */
export function substitute(template: JsonValue, params: ParamValues): JsonValue {
  if (typeof template === 'string') return substituteString(template, params);
  if (Array.isArray(template)) return template.map((item) => substitute(item, params));
  if (template !== null && typeof template === 'object') {
    const out: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(template)) out[key] = substitute(value, params);
    return out;
  }
  return template;
}

/** Substitute into an object template, keeping it an object. */
export function substituteObject(template: Readonly<Record<string, JsonValue>>, params: ParamValues): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {};
  for (const [key, value] of Object.entries(template)) out[key] = substitute(value, params);
  return out;
}
